import { buildAppUrl } from '@/utils/url';

export enum HttpStatusCode {
  // 2XX
  Ok = 200,
  Created = 201,

  // 4XX
  BadRequest = 400,
  Unauthorized = 401,
  Forbidden = 403,
  NotFound = 404,

  // 5XX
  InternalServerError = 500
}

interface ApiResponseData {
  code?: HttpStatusCode;
  status?: string;
  message?: string;
  data?: ApiResponseDataType;
}

export type ApiResponseDataType = object | string | number | boolean | null;

export type BuildApiResponseArg = ApiResponseData | number;

export function buildApiResponse(data: BuildApiResponseArg = {}) {
  if (typeof data === 'number') {
    return buildApiResponse({ code: data });
  }

  const responseCode = data.code || HttpStatusCode.Ok;

  let responseStatus = data.status;
  if (!responseStatus) {
    responseStatus = responseCode < 400 ? 'ok' : 'error';
  }

  const response = { code: responseCode, status: responseStatus } as any;

  if (data.message) {
    response.message = data.message;
  }

  if (data.data) {
    response.data = data.data;
  }

  return new Response(JSON.stringify(response), {
    status: responseCode,
    headers: {
      'Content-Type': 'application/json'
    }
  });
}

interface RequestOptions {
  method?: string;
  body?: any;
  abortController?: AbortController;
}

export interface ResponseObject {
  json(): unknown;
  text: any;
  code: HttpStatusCode;
  status: string;
  message?: string;
  data?: any;
  isError: boolean;
}

export async function sendApiRequest(
  uri: string,
  options: RequestOptions = {}
): Promise<ResponseObject> {
  const requestOptions: RequestOptions = { ...{ method: 'GET', body: undefined }, ...options };

  let requestUri = uri;
  if (!requestUri.startsWith('/')) {
    requestUri = `/${requestUri}`;
  }
  if (requestUri.endsWith('/')) {
    requestUri = requestUri.substring(0, requestUri.length - 1);
  }

  let requestBody: string | undefined;
  if (requestOptions.body && requestOptions.method !== 'GET') {
    requestBody = JSON.stringify(requestOptions.body);
  }

  const url = buildAppUrl(`api${requestUri}`);
  const fetchResponse = await fetch(url, {
    method: requestOptions.method || 'GET',
    headers: {
      'Content-Type': 'application/json'
    },
    body: requestBody
    // bit by this: https://github.com/vercel/next.js/issues/63481
    // signal: abortController.signal
  });

  const response = await fetchResponse.json();
  if (response.code) {
    response.isError = response.code >= 400;
  }
  return response;
}

export async function sendPostApiRequest(
  uri: string,
  body: any = {},
  options: RequestOptions = {}
) {
  return sendApiRequest(uri, { ...options, method: 'POST', body });
}
